import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PricingToggle } from "../components/PricingToggle";
import { Reveal } from "../components/Reveal";
import { fetchProduct, gbp } from "../lib/api";
import { useCart } from "../context/CartContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [plan, setPlan] = useState("onetime");
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct(slug).then(setProduct).catch(() => setProduct(false));
  }, [slug]);

  if (product === false) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <p className="font-serif italic text-2xl text-faded">Product not found.</p>
          <Link to="/" className="text-sm font-bold uppercase tracking-widest text-forest underline underline-offset-4">Back to shop</Link>
        </div>
      </div>
    );
  }
  if (!product) return <div className="min-h-screen bg-cream" />;

  const price = plan === "subscribe" ? product.price_subscribe : product.price_onetime;

  return (
    <div className="bg-cream">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-24">
        <Link to="/" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-faded hover:text-forest transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
        </Link>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:sticky md:top-28 h-fit rounded-[1.75rem] overflow-hidden bg-white border border-tan"
          >
            <img data-testid="detail-image" src={product.image} alt={product.name} className="w-full h-full object-contain aspect-[4/5] p-4" />
          </motion.div>

          <div className="flex flex-col">
            <Reveal>
              <p className="font-serif italic text-faded text-lg">{product.ritual} ritual · {product.flavour}</p>
              <h1 className="mt-2 font-sans font-black tracking-tight text-4xl md:text-5xl leading-[1.02] text-ink">{product.name}</h1>
              <p className="mt-5 text-faded text-lg leading-relaxed">{product.description}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap gap-2">
                {product.actives.map((a) => (
                  <span key={a} className="text-xs font-bold tracking-wide bg-tan text-forest px-3 py-1.5 rounded-full">{a}</span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 rounded-2xl border border-tan bg-offwhite p-6 md:p-7">
                <PricingToggle plan={plan} onChange={setPlan} testid={`detail-toggle-${product.slug}`} />
                <div className="mt-6 flex items-end justify-between">
                  <div className="flex flex-col">
                    <span data-testid="detail-price" className="font-sans font-black text-4xl text-ink">{gbp(price)}</span>
                    <span className="text-sm text-faded mt-1">{plan === "subscribe" ? "Delivered monthly · cancel anytime" : "One-time purchase"}</span>
                  </div>
                  <span className="text-sm text-faded">{product.count}</span>
                </div>
                <button
                  data-testid="detail-add-to-bag"
                  onClick={() => { addItem(product, plan, 1); toast.success(`${product.name} added to your bag`); }}
                  className="group mt-6 w-full flex items-center justify-center gap-3 rounded-full bg-forest text-cream px-6 py-4 text-sm font-bold tracking-widest uppercase transition-colors duration-300 hover:bg-forest-deep"
                >
                  Add to bag · {gbp(price)}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                </button>
                <p className="mt-3 text-center text-[11px] text-faded tracking-wide">Apple Pay · Google Pay · Card via Stripe</p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <ul className="mt-8 space-y-3">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-ink">
                    <span className="w-5 h-5 rounded-full bg-forest text-cream flex items-center justify-center shrink-0"><Check className="w-3 h-3" strokeWidth={3} /></span>
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.25}>
              <Accordion type="single" collapsible className="mt-8">
                <AccordionItem value="how" className="border-tan">
                  <AccordionTrigger data-testid="acc-how" className="font-sans font-bold text-ink">How to take</AccordionTrigger>
                  <AccordionContent className="text-faded">Take 1–2 gummies daily. Best enjoyed as part of your morning or evening ritual.</AccordionContent>
                </AccordionItem>
                <AccordionItem value="ship" className="border-tan">
                  <AccordionTrigger data-testid="acc-ship" className="font-sans font-bold text-ink">Shipping & subscription</AccordionTrigger>
                  <AccordionContent className="text-faded">Free UK shipping over £30. Subscriptions save 15%, ship monthly and can be paused or cancelled anytime.</AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
